import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { BooleanInput, Datagrid, RecordContextProvider, SimpleForm, TextInput, useDataProvider, useInput, useListContext, useNotify, useRecordContext, useRefresh, useResourceContext, useTranslate } from "react-admin";
import { useForm, useFormState } from 'react-final-form';
import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, LinearProgress, makeStyles, Typography, useMediaQuery } from "@material-ui/core";
import { Clear, PlaylistAdd } from '@material-ui/icons';
import moment from 'moment';

import { GAME_OFFICE_TYPES } from '@hisports/common/src/constants';
import { FF_SID_MEMBER_TYPES } from '@hisports/common/featureFlags';
import { filterOfficialParticipants } from '@hisports/scoresheet/src/util/officialParticipants';

import ListCard from '../../common/cards/ListCard';
import { DialogFormToolbar } from '../../common/dialogs/DialogForm';
import { FieldDependency } from '../../common/FieldDependency';
import { useSeason } from '../../common/inputs/SeasonSelector';
import SelectedItems from '../../common/SelectedItems';

import { useFlag } from '../../http';
import { OfficeInput } from '../offices/OfficeInput';
import { ParticipantField } from '../participants/ParticipantField';
import { ListInput } from '../lists/ListInput';
import { useParticipantMemberTypes } from '../officialOffices/OfficialGrid';
import { AffiliationField, AffiliationLabel } from '../memberTypes/AffiliationField';

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
}));

const DEFAULT_FORM_VALUES = {
  officeId: null,
  participant: null,
  includeSubOffices: false,
  today: moment().format('YYYY-MM-DD'),
};

const SUBMIT_MSGS = {
  officialOffices: {
    success: 'resources.officialOffices.notifications.officials_added',
    error: 'resources.officialOffices.notifications.add_officials_error',
  },
  listMembers: {
    success: 'resources.listMembers.notifications.list_officials_added',
    error: 'resources.listMembers.notifications.add_list_officials_error',
  },
}

const inputProps = {
  variant: 'outlined',
  margin: 'none',
  fullWidth: true,
  helperText: false,
}

const getFilters = (values) => {
  if (!values) return null;

  const { today, officeId, includeSubOffices, participant, type } = values;

  return {
    _scope: 'Tenant',
    distinct: 'participantId',
    ...(officeId && { officeId, effectiveOffices: includeSubOffices }),
    ...(type && { 'qualificationCategory.types': { inq: [type] } }),
    and: [
      { or: [{ expiry: { gte: today } }, { expiry: { eq: null } }] },
      ...(participant ? [filterOfficialParticipants(participant)] : [])
    ],
  }
}

export const SearchInput = (props) => {
  const translate = useTranslate();
  const { input: { value, onChange } } = useInput(props);

  const handleClear = () => onChange('');

  return <TextInput
    placeholder={translate('ra.input.autocomplete.type_to_search')}
    InputProps={{
      endAdornment: (
        value && <InputAdornment position="end">
          <IconButton size="small" edge="end" onClick={handleClear}>
            <Clear fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    }}
    {...props}
  />
}

export const ListLoader = ({ marginTop = 12 }) => {
  const { loading } = useListContext();
  return <LinearProgress style={{ marginTop, visibility: loading ? 'visible' : 'hidden' }} />
}

export const ListToolbar = ({ handleSearch, handleReset }) => {
  const { dirty } = useFormState();
  const translate = useTranslate();
  const classes = useStyles();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleSearch();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSearch]);

  return <div className={classes.toolbar}>
    <Button variant="outlined" size="small" onClick={handleReset} disabled={!dirty}>
      {translate('ra.action.reset')}
    </Button>
    <Button color="primary" variant="contained" size="small" onClick={handleSearch}>
      {translate('ra.action.search')}
    </Button>
  </div>
}

const SelectedOfficials = () => {
  const { values: { participantIds = [] } } = useFormState();
  const { onToggleItem, onUnselectItems } = useListContext();

  const items = participantIds?.map(participantId => ({ id: participantId, participantId })) || [];

  return <SelectedItems
    title="resources.officials.labels.selected_officials"
    noResultsText="resources.officials.messages.no_officials_selected"
    items={items}
    onRemoveAll={onUnselectItems}
    onRemove={(item) => onToggleItem(item.participantId)}
  >
    <ParticipantField source="participantId" label="resources.officials.fields.participantId" includeId="inline" link={false} />
  </SelectedItems>
};

const OfficialsGrid = (props) => {
  const seasonId = useSeason();
  const isEnabled = useFlag();
  const { selectedIds, data = {}, ids = [] } = useListContext();
  const { change } = useForm();
  const showMemberType = isEnabled(FF_SID_MEMBER_TYPES);

  const officials = useMemo(() =>
    ids.reduce((officials, id) => {
      const official = data[id];
      if (!official) return officials;

      officials[official.participantId] = { ...official, id: official.participantId };
      return officials;
    }, {})
  , [data, ids]);

  const participantIds = useMemo(() => ids.map(id => data[id]?.participantId).filter(Boolean), [ids, data]);
  const { data: memberTypes } = useParticipantMemberTypes(showMemberType, participantIds, seasonId);

  useEffect(() => {
    change('participantIds', selectedIds);
  }, [selectedIds, change]);

  return <Datagrid {...props} data={officials} ids={participantIds} size="medium" rowClick="toggleSelection" hasBulkActions>
    <ParticipantField source="participantId" includeId="inline" />
    {showMemberType && <AffiliationField label={<AffiliationLabel />} source="participantId" memberTypes={memberTypes} />}
  </Datagrid>
}

const OfficialList = () => {
  const { change, batch } = useForm();
  const { values, initialValues } = useFormState();
  const [filters, setFilters] = useState(getFilters(initialValues));

  const { officeId, includeSubOffices } = values;

  useEffect(() => {
    if (includeSubOffices && !officeId) {
      change('includeSubOffices', false);
    }
  }, [officeId, includeSubOffices, change]);

  const handleSearch = () => {
    setFilters(getFilters(values));
  };

  const handleReset = () => {
    batch(() => Object.entries(initialValues).forEach(([key, value]) => change(key, value)));
    setFilters(getFilters(initialValues));
  }

  return <ListCard
    component={Fragment}
    reference="qualifications"
    target="participantId"
    filter={filters}
    sort={{ field: 'participant.firstName', order: 'ASC' }}
    perPage={10}
    rowsPerPageOptions={[10, 25, 50]}
    additionalContent={<div style={{ paddingBottom: 16 }}>
      <ListToolbar handleSearch={handleSearch} handleReset={handleReset} />
      <SelectedOfficials />
      <ListLoader />
    </div>}
  >
    <OfficialsGrid />
  </ListCard>
}

const SelectOfficialsForm = ({ onClose, officeId, initialValues, ...props }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const onSubmit = ({ listId, participantIds }) => {
    if (!participantIds?.length) return;

    const listMembers = participantIds.map(participantId => ({
      officeId,
      participantId,
      ...(listId ? { listId } : {}),
    }));

    return dataProvider.createMany(resource, { data: listMembers })
      .then(data => data.data)
      .then(data => {
        onClose();
        refresh();
        notify(translate(SUBMIT_MSGS[resource].success, data.length || 0));
      })
      .catch(() => {
        onClose();
        notify(translate(SUBMIT_MSGS[resource].error), 'warning');
      });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const showListInput = !!initialValues?.listId;

  return <SimpleForm save={onSubmit} component={Fragment} initialValues={{ ...DEFAULT_FORM_VALUES, ...initialValues }} onKeyDown={handleKeyDown} {...props}>
    <Typography variant="subtitle1" color="textSecondary" component="h3" gutterBottom style={{ fontWeight: 500 }}>
      {translate('ra.message.filters')}
    </Typography>
    <Grid container spacing={2} alignItems="center" style={{ paddingTop: 8 }}>
      {showListInput && <Grid item xs={12}>
        <ListInput source="listId" label="resources.listMembers.fields.listId" disabled {...inputProps} />
      </Grid>}
      <Grid item xs={12} md={8}>
        <OfficeInput source="officeId" label="resources.officials.fields.qualificationOrganization" filter={{ _scope: "Tenant", type: { nin: [...GAME_OFFICE_TYPES, 'Historical'] } }} {...inputProps} helperText="ra.message.optional" />
      </Grid>
      <Grid item xs={12} md={4}>
        <FieldDependency fieldSource="officeId" disabled>
          <BooleanInput source="includeSubOffices" label="resources.officials.fields.includeSubOffices" {...inputProps} helperText />
        </FieldDependency>
      </Grid>
      <Grid item xs={12}>
        <SearchInput source="participant" label="resources.officials.fields.participant" {...inputProps} helperText="resources.officials.helpers.optional" />
      </Grid>
      <Grid item xs={12}>
        <OfficialList />
      </Grid>
    </Grid>
  </SimpleForm>
}

const SelectOfficialsDialog = ({ title, isOpen, onSubmit, onClose, ...props }) => {
  const fullScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return <Dialog open={isOpen} maxWidth="md" fullWidth fullScreen={fullScreen}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent style={{ paddingTop: 0 }}>
      <RecordContextProvider value={{}}>
        <SelectOfficialsForm
          onClose={onClose}
          toolbar={<DialogFormToolbar submitLabel="ra.action.add" cancelLabel="ra.action.cancel" onCancel={onClose} />}
          {...props}
        />
      </RecordContextProvider>
    </DialogContent>
  </Dialog>
}

export const SelectOfficialsButton = ({ size = "small", variant, ...props }) => {
  const translate = useTranslate();
  const resource = useResourceContext(props);
  const record = useRecordContext();
  const [open, setOpen] = useState(false);

  return <>
    <Button color="primary" size={size} variant={variant} startIcon={<PlaylistAdd fontSize="small" />} onClick={() => setOpen(true)}>
      {translate(`resources.${resource}.actions.add_officials`)}
    </Button>
    <SelectOfficialsDialog
      title={translate(`resources.${resource}.labels.add_officials`, { name: record?.name })}
      isOpen={open}
      onClose={() => setOpen(false)}
      {...props}
    />
  </>
}
